import { useEffect } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'

import { Button } from '@factorialco/deprecated-design-system'

import { observer } from 'mobx-react'

import Modal from 'modules/core/components/Modal'
import TextField from 'modules/core/components/TextField'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { FormErrors, useForm } from 'modules/core/hooks/useForm'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { useExpensesNavigation } from 'modules/expenses/hooks/useExpensesNavigation'
import { useCreate } from 'modules/expenses/hooks/useGroup'
import {
  AmplitudeExpenseType,
  trackAddExpensable,
} from 'modules/expenses/lib/expensables/expensableTracking'

interface GroupModalProps {
  back: { href: string }
}

interface GroupNameFieldProps {
  value?: string
  error?: string
  readonly?: boolean
  onChange: (name: string) => void
}

interface GroupFormValues {
  name: string
}

export const GroupNameField = ({ value, error, readonly, onChange }: GroupNameFieldProps) => (
  <TextField
    mandatory
    readonly={readonly}
    label={i18n.t('expenses_v2.forms.fields.expensable_group.name.label')}
    placeholder={i18n.t('expenses_v2.forms.fields.expensable_group.name.placeholder')}
    value={value}
    error={error}
    onChange={onChange}
  />
)

const GroupModal = ({ back }: GroupModalProps) => {
  const navigate = useNavigate()
  const { navigation } = useExpensesNavigation()
  const { create } = useCreate()
  const { company } = useCurrentSession()
  const [searchParams] = useSearchParams()

  const form = useForm<GroupFormValues>({
    defaultValues: {
      name: '',
    },
    validate: (data) => {
      const errors: FormErrors<GroupFormValues> = {}

      if (!data.name.trim()) {
        errors.name = i18n.t('expenses_v2.modals.new_group.errors.no_group_name')
      }

      return errors
    },
    onSubmit: async (data) => {
      const result = await create(data.name, company.id)
      const handled = handleResult(result, (formErrors) => {
        form.setErrors({ name: formErrors.name })
      })

      if (handled.success) {
        const group = handled.data.expensableGroup
        if (!group) return handled

        app.setFlash({
          type: 'correct',
          message: i18n.t('expenses_v2.modals.new_group.created_successfully'),
        })
        // TODO: track group created
        navigate(navigation.groups.id.fullPath({ groupId: group.id }))
      }

      return handled
    },
  })

  useEffect(() => {
    trackAddExpensable(AmplitudeExpenseType.Group)
  }, [])

  const onClose = () => navigate({ pathname: back.href, search: searchParams.toString() })

  return (
    <Modal
      illustration="documents-folder"
      title={i18n.t('expenses_v2.modals.new_group.title')}
      description={i18n.t('expenses_v2.modals.new_group.description')}
      onClose={onClose}
      onSubmit={() => form.handleSubmit()}
      footer={
        <>
          <Button disabled={form.isSubmitting} type="secondary" onClick={onClose}>
            {i18n.t('expenses_v2.buttons.cancel')}
          </Button>
          <Button disabled={form.isSubmitting} type="primary" submit>
            {i18n.t('expenses_v2.buttons.create')}
          </Button>
        </>
      }
    >
      <GroupNameField
        value={form.values.name}
        error={form.touched.name ? form.errors.name : undefined}
        onChange={(value) => form.setFieldValue('name', value)}
      />
    </Modal>
  )
}

export default observer(GroupModal)
